import { createSlice } from '@reduxjs/toolkit';
import AuditCampaignService from '../../services/auditCampaign';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  campaigns: [],
  campaignsTotal: 0,
  campaign: null,
  stores: [],
  storesTotal: 0,
  uploadResult: null,
  batchJob: null,
  storeAudit: null
};

// Drop empty filter values so the backend doesn't filter on blanks.
const cleanQuery = (query = {}) =>
  Object.entries(query).reduce((acc, [key, value]) => {
    if (value !== '' && value !== null && value !== undefined) acc[key] = value;
    return acc;
  }, {});

const slice = createSlice({
  name: 'auditCampaign',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getCampaignsSuccess(state, action) {
      state.isLoading = false;
      state.campaigns = action.payload.data || [];
      state.campaignsTotal = action.payload.pagination?.total || 0;
    },
    getCampaignSuccess(state, action) {
      state.isLoading = false;
      state.campaign = action.payload.data || null;
    },
    getStoresSuccess(state, action) {
      state.isLoading = false;
      state.stores = action.payload.data || [];
      state.storesTotal = action.payload.pagination?.total || 0;
    },
    setUploadResult(state, action) {
      state.isLoading = false;
      state.uploadResult = action.payload || null;
    },
    clearUploadResult(state) {
      state.uploadResult = null;
    },
    setBatchJob(state, action) {
      state.isLoading = false;
      state.batchJob = action.payload || null;
    },
    getStoreAuditSuccess(state, action) {
      state.isLoading = false;
      state.storeAudit = action.payload || null;
    },
    // Detail pages share this slice — drop the previous campaign's data so a new
    // :id never renders the one viewed before it.
    resetCampaignDetail(state) {
      state.campaign = null;
      state.stores = [];
      state.storesTotal = 0;
      state.batchJob = null;
      state.uploadResult = null;
      state.error = false;
    },
    resetStoreAudit(state) {
      state.storeAudit = null;
      state.error = false;
    }
  }
});

export default slice.reducer;

export const { clearUploadResult, resetCampaignDetail, resetStoreAudit } = slice.actions;

// ----------------------------------------------------------------------

export function getAuditCampaigns(params = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await AuditCampaignService.getList(params);
      if (response.success) dispatch(slice.actions.getCampaignsSuccess(response));
      else dispatch(slice.actions.hasError(response.errors || 'Failed to fetch campaigns'));
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch campaigns'));
    }
  };
}

export function getAuditCampaign(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await AuditCampaignService.getById(id);
      if (response.success) dispatch(slice.actions.getCampaignSuccess(response));
      else dispatch(slice.actions.hasError(response.errors || 'Failed to fetch campaign'));
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch campaign'));
    }
  };
}

export function deleteAuditCampaign(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await AuditCampaignService.remove(id);
      if (response.success) return { success: true };
      dispatch(slice.actions.hasError(response.errors || 'Failed to delete campaign'));
      return { success: false, errors: response.errors };
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to delete campaign'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function archiveAuditCampaign(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await AuditCampaignService.setArchived(id, true);
      if (response.success) return { success: true };
      dispatch(slice.actions.hasError(response.errors || 'Failed to archive campaign'));
      return { success: false, errors: response.errors };
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to archive campaign'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function unarchiveAuditCampaign(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await AuditCampaignService.setArchived(id, false);
      if (response.success) return { success: true };
      dispatch(slice.actions.hasError(response.errors || 'Failed to restore campaign'));
      return { success: false, errors: response.errors };
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to restore campaign'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function updateAuditCampaign(id, data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await AuditCampaignService.update(id, data);
      if (response.success) return { success: true, data: response.data };
      dispatch(slice.actions.hasError(response.errors || 'Failed to update campaign'));
      return { success: false, errors: response.errors };
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to update campaign'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function createAuditCampaign(data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await AuditCampaignService.create(data);
      if (response.success) {
        dispatch(slice.actions.getCampaignSuccess(response));
        return { success: true, data: response.data };
      }
      dispatch(slice.actions.hasError(response.errors || 'Failed to create campaign'));
      return { success: false, errors: response.errors };
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to create campaign'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function uploadAuditFile(id, file) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await AuditCampaignService.uploadFile(id, file);
      if (response.success) {
        dispatch(slice.actions.setUploadResult(response.data));
        return { success: true, data: response.data };
      }
      dispatch(slice.actions.hasError(response.errors || 'Failed to upload file'));
      return { success: false, errors: response.errors };
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to upload file'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function confirmAuditImport(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await AuditCampaignService.confirmImport(id);
      if (response.success) {
        // Import auto-submits every eligible store to one batch — surface it.
        if (response.data?.auto_audit?.batch_job_id) {
          dispatch(slice.actions.setBatchJob(response.data.auto_audit));
        }
        return { success: true, data: response.data };
      }
      dispatch(slice.actions.hasError(response.errors || 'Failed to confirm import'));
      return { success: false, errors: response.errors };
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to confirm import'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function submitBatchAudit(campaignId, data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await AuditCampaignService.submitBatchAudit(campaignId, data);
      if (response.success) {
        dispatch(slice.actions.setBatchJob(response.data));
        return { success: true, data: response.data };
      }
      dispatch(slice.actions.hasError(response.errors || 'Failed to submit batch audit'));
      return { success: false, errors: response.errors };
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to submit batch audit'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function getLatestBatchJob(campaignId) {
  return async (dispatch) => {
    try {
      const response = await AuditCampaignService.getLatestBatchJob(campaignId);
      // Only surface a real job; clear the panel when this campaign has none so a
      // previous campaign's job can't linger (and keep polling) here.
      if (response.success) {
        dispatch(slice.actions.setBatchJob(response.data?.batch_job_id ? response.data : null));
      }
      return response;
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function getBatchJob(batchJobId) {
  return async (dispatch) => {
    try {
      const response = await AuditCampaignService.getBatchJob(batchJobId);
      if (response.success) {
        dispatch(slice.actions.setBatchJob(response.data));
        return { success: true, data: response.data };
      }
      return { success: false, errors: response.errors };
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function syncBatchResults(batchJobId) {
  return async () => {
    try {
      const response = await AuditCampaignService.syncBatchResults(batchJobId);
      return response.success
        ? { success: true, data: response.data }
        : { success: false, errors: response.errors };
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function getStoreAudit(storeId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await AuditCampaignService.getStoreAudit(storeId);
      if (response.success) dispatch(slice.actions.getStoreAuditSuccess(response.data));
      else dispatch(slice.actions.hasError(response.errors || 'Failed to fetch audit'));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch audit'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function approveAllAudits(campaignId) {
  return async () => {
    try {
      const response = await AuditCampaignService.approveAllAudits(campaignId);
      return response.success
        ? { success: true, data: response.data }
        : { success: false, errors: response.errors };
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function approveAudit(storeId) {
  return async () => {
    try {
      const response = await AuditCampaignService.approveAudit(storeId);
      return response.success
        ? { success: true, data: response.data }
        : { success: false, errors: response.errors };
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function regenerateAudit(storeId, data) {
  return async () => {
    try {
      const response = await AuditCampaignService.regenerateAudit(storeId, data);
      return response.success
        ? { success: true, data: response.data }
        : { success: false, errors: response.errors };
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function saleshandyPreview(campaignId, data) {
  return async () => {
    try {
      const response = await AuditCampaignService.saleshandyPreview(campaignId, data);
      return response.success
        ? { success: true, data: response.data }
        : { success: false, errors: response.errors };
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function saleshandySend(campaignId, data, idempotencyKey) {
  return async () => {
    try {
      const response = await AuditCampaignService.saleshandySend(campaignId, data, idempotencyKey);
      return response.success
        ? { success: true, data: response.data }
        : { success: false, errors: response.errors };
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function getSaleshandyImports(campaignId, params = {}) {
  return async () => {
    try {
      const response = await AuditCampaignService.getSaleshandyImports(campaignId, params);
      return response.success
        ? { success: true, data: response.data }
        : { success: false, errors: response.errors };
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function getSaleshandyImportJob(importJobId) {
  return async () => {
    try {
      const response = await AuditCampaignService.getSaleshandyImportJob(importJobId);
      return response.success
        ? { success: true, data: response.data }
        : { success: false, errors: response.errors };
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function refreshSaleshandyImport(importJobId) {
  return async () => {
    try {
      const response = await AuditCampaignService.refreshSaleshandyImport(importJobId);
      return response.success
        ? { success: true, data: response.data }
        : { success: false, errors: response.errors };
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function resubmitSaleshandyImport(importJobId) {
  return async () => {
    try {
      const response = await AuditCampaignService.resubmitSaleshandyImport(importJobId);
      return response.success
        ? { success: true, data: response.data }
        : { success: false, errors: response.errors };
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function getSaleshandyImportStores(importJobId) {
  return async () => {
    try {
      const response = await AuditCampaignService.getSaleshandyImportStores(importJobId);
      return response.success
        ? { success: true, data: response.data }
        : { success: false, errors: response.errors };
    } catch (error) {
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function getAuditStores(id, params = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await AuditCampaignService.getStores(id, cleanQuery(params));
      if (response.success) dispatch(slice.actions.getStoresSuccess(response));
      else dispatch(slice.actions.hasError(response.errors || 'Failed to fetch stores'));
    } catch (error) {
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch stores'));
    }
  };
}
